import React, { forwardRef, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Button, HStack, Text } from '@chakra-ui/react';
import { colors } from '../../../../App/ChakraTheme';

import Loader from '../../../../components/Loader';
import useInteractiveQuiz from './hooks/useInteractiveQuiz';
import useConditionalBlock from './hooks/useConditionalBlock';
import useFileUploadBlock from './hooks/useFileUploadBlock.tsx';
import useEmbedEditor from './hooks/useEmbedEditor';
import useWindowSize from '../../hooks/useWindowSize';
import { useQueryParam } from '../../../../hooks/useQueryParams';
import updateQueryParams from '../../../../helpers/updateQueryParams';

import { trackAction } from '../../../../helpers/trackAction';
import { CheckbocCheckedIcon, CheckboxIcon, LongArrowLeftIcon } from '../../../../components/icons';

const goToTop = () => {
  window.scrollTo({ top: 0, behavior: 'smooth' });
};

const timestamp = new Date().getDate();

const CmsContent = forwardRef((props, iframeRef) => {
  const {
    content,
    isChalkedUp,
    studyGroup,
    isChalkupsLoading,
    toggleComplete,
    goToNextResource,
    isTeacherGuideDisplayed,
    showAllSections,
    isUsingMaxWidth,
  } = props;
  const realIFrame = useRef(null);
  const [totalSections, setTotalSections] = useState(0);
  const qSectionNum = +useQueryParam('current-section') || 0;
  const [currentSectionCounter, setCurrentSectionCounter] =
    useState(qSectionNum);
  const [iframeHeight, setIframeHeight] = useState(1000);
  const [isLoading, setIsLoading] = useState(true);
  const location = useLocation();
  const currentSectionInterval = useRef(null);

  const { width, height } = useWindowSize();
  useInteractiveQuiz({
    content,
    studyGroup,
    currentSectionCounter,
    iFrame: realIFrame,
    isContentLoading: isLoading,
  });
  useFileUploadBlock({
    content,
    studyGroup,
    currentSectionCounter,
    iFrame: realIFrame,
    isContentLoading: isLoading,
  });
  useEmbedEditor({
    currentSectionCounter,
    iFrame: realIFrame,
    isContentLoading: isLoading,
  });
  useConditionalBlock({
    iFrame: realIFrame,
    isContentLoading: isLoading,
    updateContentSections: () => {
      const sections = getSections();
      setTotalSections(sections.length || 1);
      if (sections.length < currentSectionCounter) setCurrentSectionCounter(0);
      displayCurrentSection();
    },
  });

  useEffect(() => {
    setTimeout(() => {
      resizeIFrameToFitContent();
    }, 1000);
    setInterval(() => {
      resizeIFrameToFitContent();
    }, 5000);
  }, []);

  useEffect(() => {
    resizeIFrameToFitContent();
  }, [width, height]);

  useEffect(() => {
    handleTeacherGuide();
  }, [isTeacherGuideDisplayed]);

  const displayCurrentSection = () => {
    if (showAllSections) {
      const sections = getSections();
      sections.forEach((section, index) => {
        section.classList.remove('content_section_hidden');
      });
      return;
    }
    stopAllVideos();
    const sections = getSections();
    sections.forEach((section, index) => {
      section.classList.add('content_section_hidden');
    });
    sections.forEach((section, index) => {
      if (index === currentSectionCounter)
        section.classList.remove('content_section_hidden');
    });
  };

  const handleTeacherGuide = () => {
    const iframeDoc = realIFrame.current.contentWindow.document;
    const teacherGuides = iframeDoc.querySelectorAll('.teacher_guide');
    teacherGuides.forEach((teacherGuide, index) => {
      teacherGuide.style.display = isTeacherGuideDisplayed ? 'block' : 'none';
    });
    resizeIFrameToFitContent();
  };

  const stopAllVideos = () => {
    realIFrame.current.contentWindow.postMessage('StopAllVideos');
  };

  const changeLinks = () => {
    const iframeDoc = realIFrame.current.contentWindow.document;
    iframeDoc.querySelectorAll('a').forEach((a) => {
      const href = a.getAttribute('href');
      if (href && href.startsWith('/this')) {
        const base = location.pathname.split('/').slice(0, 3);
        const newPath = href.split('/').slice(2);

        a.href = [...base, ...newPath].join('/');
      }
    });
  };

  const setupActionTracking = () => {
    const iframeDoc = realIFrame.current.contentWindow.document;
    const codeBlocks = iframeDoc.querySelectorAll('.code-block');
    codeBlocks.forEach((codeBlock, index) => {
      const btn = codeBlock.querySelector('button.copy-button');
      const name = codeBlock.querySelector('h2')?.innerHTML;
      const id = codeBlock.dataset.codeBlockId;
      // output on code-block doesn't have a copy btn
      if (btn)
        btn.addEventListener('click', () => {
          trackAction({
            action_type: 'copy_code',
            action_object: { type: 'content', pk: content.pk },
            data: { name, id },
            study_group: { pk: studyGroup.pk },
          });
        });
    });
    realIFrame.current.contentWindow.addEventListener('message', (e) => {
      if (e.data.event && e.data.event === 'play_video')
        trackAction({
          action_type: 'play_video',
          action_object: { type: 'content', pk: content.pk },
          data: { id: e.data.id, src: e.data.src },
          study_group: { pk: studyGroup.pk },
        });
    });
  };

  useEffect(() => {
    displayCurrentSection();
    resizeIFrameToFitContent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSectionCounter]);

  const resizeIFrameToFitContent = () => {
    if (!realIFrame.current?.contentWindow.document.body) {
      return;
    }
    const h = realIFrame.current.contentWindow.document.body.scrollHeight;
    setIframeHeight(h);

    realIFrame.current.height = h;
    realIFrame.current.style.minHeight = h + ' px !important';
  };

  const getSections = () => {
    const iframeDoc = realIFrame.current.contentWindow.document;
    return iframeDoc.querySelectorAll('.content_section');
  };

  useEffect(() => {
    if (currentSectionInterval.current) {
      clearInterval(currentSectionInterval.current);
    }
    if (isLoading) {
      currentSectionInterval.current = setInterval(() => {
        if (isLoading) {
          if (!realIFrame.current || !content) {
            return;
          }
          const sections = getSections();
          setTotalSections(sections.length || 1);

          if (sections.length < currentSectionCounter)
            setCurrentSectionCounter(0);

          displayCurrentSection();
          resizeIFrameToFitContent();
          handleTeacherGuide();
        }
      }, 500);
    }
    return () => {
      if (currentSectionInterval.current) {
        clearInterval(currentSectionInterval.current);
      }
    };
  }, [isLoading]);

  const onCmsContentLoad = () => {
    setIsLoading(false);
    if (!realIFrame.current || !content) {
      return;
    }
    const sections = getSections();
    setTotalSections(sections.length || 1);
    if (sections.length < currentSectionCounter) setCurrentSectionCounter(0);
    displayCurrentSection();
    resizeIFrameToFitContent();
    handleTeacherGuide();
    setupActionTracking();
    changeLinks();
  };

  const isBackDisabled = currentSectionCounter === 0;
  const isNextDisabled =
    currentSectionCounter === totalSections - 1 && !isChalkedUp;

  const next = () => {
    if (isNextDisabled) {
      return;
    }
    goToTop();
    if (currentSectionCounter === totalSections - 1 && isChalkedUp) {
      trackAction({
        action_type: 'next',
        action_object: {
          type: 'content',
          pk: content.pk,
        },
        study_group: { pk: studyGroup.pk },
        data: {
          sectionCounter: currentSectionCounter,
        },
      });
      goToNextResource();
      setCurrentSectionCounter(0);
    } else {
      trackAction({
        action_type: 'next',
        action_object: {
          type: 'content',
          pk: content.pk,
        },
        study_group: { pk: studyGroup.pk },
        data: {
          sectionCounter: currentSectionCounter,
          nextSectionCounter: currentSectionCounter + 1,
        },
      });
      const queryParams = new URLSearchParams(location.search);
      queryParams.set('current-section', currentSectionCounter + 1);
      updateQueryParams(queryParams);
      setCurrentSectionCounter((c) => c + 1);
    }
  };

  const back = () => {
    if (isBackDisabled) {
      return;
    }
    trackAction({
      action_type: 'previous',
      action_object: {
        type: 'content',
        pk: content.pk,
      },
      study_group: { pk: studyGroup.pk },
      data: {
        sectionCounter: currentSectionCounter,
        nextSectionCounter: currentSectionCounter - 1,
      },
    });
    const queryParams = new URLSearchParams(location.search);
    queryParams.set('current-section', currentSectionCounter - 1);
    updateQueryParams(queryParams);
    setCurrentSectionCounter((c) => c - 1);
    goToTop();
  };

  useEffect(() => {
    setCurrentSectionCounter(qSectionNum);
  }, [content?.id]);

  const NavigationRow = () => {
    return (
      totalSections > 0 && (
        <HStack
          w="full"
          justify="space-between"
          align="center"
          style={{
            marginBottom: 40,
            marginTop: 20,
            paddingLeft: 30,
            paddingRight: 30,
          }}
          maxW={isUsingMaxWidth ? '1200px' : undefined}
        >
          <Button
            colorScheme="blue"
            disabled={isBackDisabled}
            onClick={() => back()}
            style={{ marginRight: 16 }}
            leftIcon={<LongArrowLeftIcon boxSize="24px" />}
          >
            <span className={'back'}>Previous</span>
          </Button>
          <Text as="h4" textStyle="h4" mr={4}>
            {currentSectionCounter + 1} / {totalSections}
          </Text>
          {isNextDisabled ? (
            isChalkupsLoading ? (
              <Button colorScheme="blue" title="loading" onClick={() => { }}>
                <Loader />
              </Button>
            ) : (
              <Button
                className="complete-btn"
                colorScheme="blue"
                variant="outline"
                style={isChalkedUp ? { background: colors.limegreen } : {}}
                title={
                  isChalkedUp ? 'content is completed' : 'mark as complete'
                }
                onClick={() => {
                  toggleComplete(true);
                  goToTop();
                  // goToNextResource();
                }}
                leftIcon={isChalkedUp ? (<CheckbocCheckedIcon boxSize="24px" />)
                  : (<CheckboxIcon boxSize="24px" />)}
              >

                <span className={'hide-tablet'}>
                  {isChalkedUp ? 'Completed' : 'Complete'}
                </span>
              </Button>
            )
          ) : (
            <Button colorScheme="blue" disabled={isNextDisabled} onClick={() => next()} rightIcon={<LongArrowLeftIcon boxSize="24px" transform="rotate(180deg)" />}>
              <span className={'back'}>Next</span>
            </Button>
          )}
        </HStack>
      )
    );
  };

  return (
    <div
      ref={iframeRef}
      className="cms_content_container"
      style={{
        padding: showAllSections ? '40px' : undefined,
        maxWidth: isUsingMaxWidth ? '1200px' : undefined,
        marginLeft: isUsingMaxWidth ? 'auto' : undefined,
        marginRight: isUsingMaxWidth ? 'auto' : undefined,
      }}
    >
      {showAllSections ? null : <NavigationRow />}

      <iframe
        title={content['title']}
        ref={realIFrame}
        height={iframeHeight}
        style={{
          height: iframeHeight,
          marginBottom: 40,
          overflow: 'hidden',
          borderBottom: '1px solid rgba(213,214,222,0.5)',
          boxSizing: 'content-box',
        }}
        width="100%"
        src={`/cms_content/${content['pk']}/?toolbar_off&timestamp=${timestamp}`}
        frameBorder="0"
        allowFullScreen
        className="cms_content"
        onLoad={() => onCmsContentLoad()}
      />
      {isLoading && <Loader />}
      {showAllSections
        ? null
        : iframeHeight > 0.7 * height && <NavigationRow />}
    </div>
  );
});
export default CmsContent;
